<template>
  <div class="linked-operator-selector">
    <label class="linked-operator-selector__label">
      {{ t('automatedScenarios.filters.linkedOperators.label') }}
    </label>
    
    <!-- Selected operators display -->
    <div v-if="selectedOperators.length > 0" class="linked-operator-selector__selected">
      <div
        v-for="op in selectedOperators"
        :key="op.stepId"
        class="linked-operator-selector__selected-item"
      >
        <div class="linked-operator-selector__selected-icon">
          <i :class="`far ${op.icon}`" />
        </div>
        <div class="linked-operator-selector__selected-info">
          <span class="linked-operator-selector__selected-label">{{ op.label }}</span>
          <span v-if="op.customTitle" class="linked-operator-selector__selected-title">
            {{ op.customTitle }}
          </span>
        </div>
        <Button
          icon="far fa-times"
          class="p-button-text p-button-rounded p-button-sm linked-operator-selector__remove-btn"
          @click="removeSelection(op.stepId)"
        />
      </div>
    </div>
    
    <!-- Select button -->
    <Button
      :label="selectedOperators.length > 0 
        ? t('automatedScenarios.filters.linkedOperators.modify_selection') 
        : t('automatedScenarios.filters.linkedOperators.select_on_flow')"
      :icon="selectedOperators.length > 0 ? 'far fa-edit' : 'far fa-mouse-pointer'"
      class="p-button-outlined linked-operator-selector__select-btn"
      @click="openFlowSelection"
    />
    
    <FieldErrors
      v-if="formValidation"
      :errors="formValidation"
      field="linked_box"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed, inject, ComputedRef, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import { getOperatorById, OperatorInsertContext } from '@/components/flows/operators/registry';
import { parseOperatorParameters } from '@/helpers/flowParameters';
import { startLinkedOperatorSelection, consumeLastConfirmedSelection } from '@/composables/flows/useLinkedOperatorSelection';
import type { FlowStructureDto } from '@/types/flow-structure';

export interface LinkableOperator {
  stepId: string;
  type: string;
  icon: string;
  label: string;
  customTitle?: string;
}

export default defineComponent({
  name: 'LinkedOperatorSelector',

  components: {
    Button,
    FieldErrors,
  },

  props: {
    modelValue: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    
    // List of operator types that can be linked (e.g., ['boxsendemail', 'boxsendfbmessenger'])
    linkableTypes: {
      type: Array as PropType<string[]>,
      required: true,
    },
    
    // Current operator's step ID (to find ancestors) - used in edit mode
    operatorId: {
      type: String,
      default: '',
    },
    
    formValidation: {
      type: Object,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();
    
    // Inject flow and insert context from parent
    const flow = inject<ComputedRef<FlowStructureDto | null>>('flow', computed(() => null));
    const insertContext = inject<ComputedRef<OperatorInsertContext | null>>('insertContext', computed(() => null));

    // Get operator info from step ID
    const getOperatorInfo = (stepId: string): LinkableOperator | null => {
      if (!flow.value) return null;
      
      const step = flow.value.steps.find(s => s.id === stepId);
      if (!step) return null;
      
      const metadata = getOperatorById(step.type);
      const params = parseOperatorParameters(step.parameters);
      
      return {
        stepId: step.id,
        type: step.type,
        icon: metadata?.icon ?? 'fa-box',
        label: metadata ? t(metadata.label) : step.type,
        customTitle: params.custom_title as string || undefined,
      };
    };

    // Get selected operators with their info
    const selectedOperators = computed<LinkableOperator[]>(() => {
      if (!props.modelValue.length) return [];
      
      // If flow is not available, show basic info with step IDs
      if (!flow.value) {
        return props.modelValue.map(stepId => ({
          stepId,
          type: 'unknown',
          icon: 'fa-box',
          label: stepId,
          customTitle: undefined,
        }));
      }
      
      return props.modelValue
        .map(stepId => getOperatorInfo(stepId))
        .filter((op): op is LinkableOperator => op !== null);
    });

    // Remove a selection
    const removeSelection = (stepId: string) => {
      const newValue = props.modelValue.filter(id => id !== stepId);
      emit('update:modelValue', newValue);
    };

    // Inject callbacks from parent
    const reopenPanel = inject<(() => void) | null>('reopenPanel', null);
    const handleStartSelection = inject<(() => void) | null>('handleStartSelection', null);

    // Get all ancestor step IDs for a given step (for restricting selection to ancestors only)
    const getAncestorStepIds = (stepId: string): string[] => {
      if (!flow.value) return [];
      const ancestorIds: string[] = [];
      const visited = new Set<string>();

      const collectAncestors = (currentId: string) => {
        if (visited.has(currentId)) return;
        visited.add(currentId);

        const step = flow.value!.steps.find(s => s.id === currentId);
        if (!step) return;

        // Add primary parent
        if (step.primaryParent?.id) {
          ancestorIds.push(step.primaryParent.id);
          collectAncestors(step.primaryParent.id);
        }

        // Add secondary parents
        step.parents?.forEach(parent => {
          if (parent.id && !ancestorIds.includes(parent.id)) {
            ancestorIds.push(parent.id);
            collectAncestors(parent.id);
          }
        });
      };

      collectAncestors(stepId);
      return ancestorIds;
    };

    // Get selectable step IDs (ancestors of the current operator being configured)
    const getSelectableStepIds = (): string[] => {
      // If we have an operatorId (edit mode), use its ancestors
      if (props.operatorId) {
        return getAncestorStepIds(props.operatorId);
      }
      // If we have insert context with allAncestorStepIds, use it directly
      const context = insertContext.value;
      if (context?.allAncestorStepIds && context.allAncestorStepIds.length > 0) {
        return context.allAncestorStepIds;
      }
      return [];
    };

    // Check for pending confirmed selection when component mounts (after panel reopens)
    onMounted(() => {
      const confirmedSelection = consumeLastConfirmedSelection();
      if (confirmedSelection !== null) {
        emit('update:modelValue', confirmedSelection);
      }
    });

    // Open flow selection mode
    const openFlowSelection = () => {
      // Get the list of ancestor step IDs that can be selected
      const selectableStepIds = getSelectableStepIds();
      
      // Start the selection mode with callbacks
      startLinkedOperatorSelection(
        props.linkableTypes,
        selectableStepIds,
        [...props.modelValue],
        (selectedStepIds: string[]) => {
          // On confirm: update the model value
          emit('update:modelValue', selectedStepIds);
        },
        () => {
          // On cancel: do nothing, keep current selection
        },
        reopenPanel || undefined
      );
      
      // Hide the panel when entering selection mode
      if (handleStartSelection) {
        handleStartSelection();
      }
    };

    return {
      t,
      selectedOperators,
      removeSelection,
      openFlowSelection,
    };
  },
});
</script>

<style lang="scss" scoped>
.linked-operator-selector {
  &__label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #374151;
  }

  &__selected {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }

  &__selected-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
    background: #eff6ff;
    border: 1px solid #3b82f6;
    border-radius: 0.5rem;
  }

  &__selected-icon {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #3b82f6;
    border-radius: 0.375rem;
    color: white;
    font-size: 0.875rem;
  }

  &__selected-info {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  &__selected-label {
    font-weight: 500;
    color: #1e40af;
    font-size: 0.875rem;
  }

  &__selected-title {
    font-size: 0.75rem;
    color: #3b82f6;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__remove-btn {
    flex-shrink: 0;
    width: 24px !important;
    height: 24px !important;
    color: #6b7280 !important;

    &:hover {
      color: #ef4444 !important;
      background: rgba(239, 68, 68, 0.1) !important;
    }
  }

  &__select-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
